import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import App from './App.vue'
import { vPrefetch } from './composables/usePrefetch'

// One-shot self-heal for users whose browser still has an old service worker
// and/or cached bundles from a previous deploy. Unregistering the worker
// alone is not enough - CacheStorage entries persist and the already-running
// page was served from the SW cache before this code could run. We also
// clear every cache AND, if any SW / cache actually existed, force a single
// reload so the user ends up on the fresh bundle. We flag the reload in
// sessionStorage so we never loop.
if (typeof window !== 'undefined') {
  const RELOAD_FLAG = '__llCacheBustedOnce'
  const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG)

  const clearSWAndCaches = async () => {
    let didClean = false
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        for (const reg of regs) {
          await reg.unregister()
          didClean = true
          console.log('[SW] Unregistered stale service worker:', reg.scope)
        }
      }
      if ('caches' in window) {
        const keys = await caches.keys()
        for (const key of keys) {
          await caches.delete(key)
          didClean = true
          console.log('[SW] Deleted cache:', key)
        }
      }
    } catch (e) {
      console.warn('[SW] Cleanup failed', e)
    }

    if (didClean && !alreadyReloaded) {
      sessionStorage.setItem(RELOAD_FLAG, '1')
      console.log('[SW] Reloading page with fresh bundles')
      window.location.reload()
    }
  }

  clearSWAndCaches()
}

// Bootstrap CSS and Icons (bundled locally)
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap-icons/font/bootstrap-icons.min.css'

// Bootstrap JS (includes Popper)
import 'bootstrap/dist/js/bootstrap.bundle.min.js'

import './assets/styles.css'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)

// Register prefetch directive for McMaster-Carr style link prefetching
// Usage: <router-link v-prefetch to="/path">Link</router-link>
app.directive('prefetch', vPrefetch)

// Add DNS prefetch hints for external APIs
const dnsPrefetchDomains = [
  'api.twitch.tv',
  'id.twitch.tv',
  'discord.com',
  'cdn.discordapp.com'
]

dnsPrefetchDomains.forEach(domain => {
  const link = document.createElement('link')
  link.rel = 'dns-prefetch'
  link.href = `//${domain}`
  document.head.appendChild(link)

  // Also add preconnect for faster connections
  const preconnect = document.createElement('link')
  preconnect.rel = 'preconnect'
  preconnect.href = `https://${domain}`
  preconnect.crossOrigin = 'anonymous'
  document.head.appendChild(preconnect)
})

app.mount('#app')
